import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Box, 
  TextField, 
  IconButton, 
  Typography, 
  Paper, 
  CircularProgress,
  Tooltip,
  Menu,
  MenuItem,
  FormControl,
  Select,
  Chip,
  Divider,
  LinearProgress,
  Collapse
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TimelineIcon from '@mui/icons-material/Timeline';
import DownloadIcon from '@mui/icons-material/Download';
import { exportConversation } from '../../utils/exportChat';
import useChatAgent from '../../hooks/useChatAgent';
import useAiModels from '../../hooks/useAiModels';
import ApiKeySettings from './ApiKeySettings';
import ProviderSelector from './ProviderSelector';
import PropTypes from 'prop-types';
import { chatApi } from '../../services/chatapi';
import { chatInputKeyAction } from '../../utils/chatInputKeys';
import ErrorAlert from '../common/ErrorAlert';
import ProcessArea from './ProcessArea';
import RequirementText from '../common/RequirementText';

// Frozen module-level singletons for empty default props (#943,
// react/no-object-type-as-default-prop). A `= []` / `= {}` default builds a NEW
// value on every render, so a memoised child or an effect dependency sees a change
// that never happened.
const EMPTY_ARRAY = Object.freeze([]);

/**
 * ThinkingStep component for displaying individual thinking steps with collapsible content
 */
const ThinkingStep = ({ step, stepNumber, totalSteps, requirements = EMPTY_ARRAY }) => {
  const [expanded, setExpanded] = useState(false);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (!step.timestamp) return undefined;
    const compute = () => {
      const diff = Date.now() - new Date(step.timestamp).getTime();
      setDuration(Math.max(0, Math.floor(diff / 1000))); // seconds since the step started
    };
    compute();
    // Only the latest (still-active) step keeps ticking; completed steps freeze
    // their elapsed time. This bounds the app to a single running timer instead
    // of one permanent setInterval per step.
    if (stepNumber !== totalSteps) return undefined;
    const interval = setInterval(compute, 1000);
    return () => clearInterval(interval);
  }, [step.timestamp, stepNumber, totalSteps]);

  const getStepIcon = (type) => {
    switch (type) {
      case 'tool_use': return '🔧';
      case 'observation': return '👁️';
      case 'iteration': return '🔄';
      default: return '💭';
    }
  };

  const getStepColor = (type) => {
    switch (type) {
      case 'tool_use': return '#2196f3';
      case 'observation': return '#4caf50';
      case 'iteration': return '#ff9800';
      default: return '#9e9e9e';
    }
  };

  const getStepTitle = (type) => {
    switch (type) {
      case 'tool_use': return 'Tool Usage';
      case 'observation': return 'Observation';
      case 'iteration': return 'Iteration';
      default: return 'Thought';
    }
  };

  return (
    <Paper
      elevation={1}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: 2,
          borderColor: getStepColor(step.type)
        }
      }}
    >
      <Box
        sx={{
          p: 1.5,
          bgcolor: 'background.paper',
          borderBottom: expanded ? '1px solid' : 'none',
          borderBottomColor: 'divider',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 'bold',
              color: getStepColor(step.type),
              mr: 1,
              minWidth: '24px'
            }}
          >
            {stepNumber}.
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 'medium',
              color: 'text.primary',
              mr: 1,
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {getStepIcon(step.type)} {getStepTitle(step.type)}
          </Typography>
          {duration > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
              <AccessTimeIcon fontSize="small" sx={{ color: 'text.secondary', mr: 0.5 }} />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {duration}s
              </Typography>
            </Box>
          )}
        </Box>
        <IconButton size="small" sx={{ p: 0.5 }}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ p: 2, bgcolor: 'background.default' }}>
          <RequirementText
            text={step.content}
            requirements={requirements}
            onRequirementClick={(requirementId) => {
              console.log('Requirement clicked in thinking step:', requirementId);
              // TODO: Implement navigation to requirement details
            }}
            sx={{
              whiteSpace: 'pre-wrap',
              lineHeight: 1.5,
              '& code': {
                bgcolor: 'background.paper',
                p: '2px 4px',
                borderRadius: '3px',
                fontSize: '0.8rem',
                fontFamily: 'Monaco, Menlo, monospace',
                border: '1px solid',
                borderColor: 'divider'
              }
            }}
          />
        </Box>
      </Collapse>
    </Paper>
  );
};

ThinkingStep.propTypes = {
  step: PropTypes.object.isRequired,
  stepNumber: PropTypes.number.isRequired,
  totalSteps: PropTypes.number.isRequired,
  requirements: PropTypes.array
};

export { ThinkingStep }; // Export for testing

/**
 * ChatInterface component provides an interactive chat UI for AI-powered conversations.
 *
 * @param {Object} props
 * @param {Object|null} props.context - Optional context object passed to the chat agent.
 * @param {Function|null} props.onContextChange - Callback invoked when the context changes.
 * @param {string} props.height - Height of the chat interface.
 * @param {boolean} props.initialOpen - Whether the chat is initially expanded.
 * @param {Function|null} props.onResponse - Optional callback invoked when the AI assistant sends a response.
 *        Receives one argument: the response message object ({ id, role, content, ... }).
 *        Called after a successful response is received from the backend.
 */
const ChatInterface = ({ 
  context = null, 
  onContextChange = null,
  height = '100%',
  initialOpen = false,
  onResponse = null,
  requirements = EMPTY_ARRAY
}) => {
  const { 
    messages, 
    loading, 
    error, 
    streamingMessage,
    thinkingSteps,
    showThinking,
    toggleThinking,
    currentIteration,
    maxIterations,
    sendStreamingMessage, 
    clearHistory, 
    clearError,
    startNewSession
  } = useChatAgent(onResponse);
  
  const { models, currentModel, changeModel } = useAiModels();

  const [inputValue, setInputValue] = useState('');
  const [expanded, setExpanded] = useState(initialOpen);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  const [inlineErrorMessage, setInlineErrorMessage] = useState(null);
  const [showProcessArea, setShowProcessArea] = useState(true);
  
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, streamingMessage, inlineErrorMessage, thinkingSteps]);
  
  useEffect(() => {
    if (error) {
      setInlineErrorMessage(error);
    } else {
      setInlineErrorMessage(null);
    }
  }, [error]);
  
  useEffect(() => {
    if (context) {
      chatApi.logMessage('Chat context updated:', context);
    }
  }, [context]);
  
  useEffect(() => {
    if (context && onContextChange) {
      chatApi.logMessage('Passing context to chat agent:', context);
      onContextChange(context);
    }
  }, [context, onContextChange]);
  
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!inputValue.trim()) return;
    
    try {
      chatApi.logMessage('Sending message with context:', {
        message: inputValue, 
        context: context || 'No context provided'
      });
      
      setInlineErrorMessage(null);
      
      await sendStreamingMessage(inputValue, context);
      setInputValue('');
      
      if (inputRef.current) {
        inputRef.current.focus();
      }
    } catch (err) {
      console.error('Error sending message:', err);
      chatApi.logMessage('Error sending message:', err);
    }
  };
  
  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };
  
  const handleClearHistory = async () => {
    await clearHistory();
    handleMenuClose();
  };

  const handleExportChat = () => {
    exportConversation(messages);
    handleMenuClose();
  };
  
  const handleNewSession = () => {
    startNewSession();
    handleMenuClose();
  };
  
  const handleErrorDismiss = () => {
    clearError();
    setInlineErrorMessage(null);
  };
  
  // #170: single-line status string for the screen-reader live region below.
  // The visible thinking UI conveys progress by animation/spinner and the send
  // button just disables — none of which a screen reader announces. Derive a
  // plain-text state so `aria-live="polite"` can announce it: "thinking" while a
  // request is in flight with no assistant text yet, "responding" once tokens
  // start streaming, and empty (silence) when idle.
  const aiStatus = (() => {
    if (!loading) return '';
    if (streamingMessage && (streamingMessage.content || '').trim()) return 'AI is responding…';
    return 'AI is thinking…';
  })();

  const handleToggleThinking = () => {
    toggleThinking();
    handleMenuClose();
  };
  
  const handleKeyDown = (e) => {
    const action = chatInputKeyAction(e, {
      hasText: Boolean(inputValue.trim()),
      hasMessages: messages.length > 0,
    });

    // Enter sends; Shift+Enter ('newline') falls through to the textarea so the
    // input stays truly multi-line (T1.11).
    if (action === 'send') {
      e.preventDefault();
      if (inputValue.trim() && !loading) {
        handleSubmit(e);
      }
      return;
    }

    // Recall last message when pressing up arrow in an empty input field.
    if (action === 'recall') {
      const userMessages = messages.filter(msg => msg.role === 'user');
      if (userMessages.length > 0) {
        const lastUserMessage = userMessages[userMessages.length - 1];
        setInputValue(lastUserMessage.content);
        // Place cursor at the end of the text
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.setSelectionRange(
              lastUserMessage.content.length,
              lastUserMessage.content.length
            );
          }
        }, 0);
      }
    }
  };
  
  const renderMessage = (message) => {
    return (
      <Paper 
        key={message.id}
        elevation={0} 
        sx={{ 
          p: 2, 
          mb: 2, 
          bgcolor: message.role === 'user' ? 'background.default' : 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          maxWidth: '85%',
          ml: message.role === 'user' ? 'auto' : 2,
          mr: message.role === 'user' ? 2 : 'auto',
        }}
      >
        <Typography 
          variant="caption" 
          sx={{ display: 'block', mb: 0.5, fontWeight: message.role === 'user' ? 'bold' : 'normal', color: 'text.secondary' }}
        >
          {message.role === 'user' ? 'You' : 'AI Assistant'}
        </Typography>
        
        <Box sx={{ 
          '& p': { m: 0, mb: 1 },
          '& pre': { my: 1, p: 0 },
          '& code': { 
            bgcolor: 'background.paper', 
            px: 0.5, 
            py: 0.25, 
            borderRadius: 0.5,
            fontFamily: 'monospace',
            fontSize: '0.85rem'
          },
          '& ul, & ol': { pl: 3, mb: 1 },
          '& h1, & h2, & h3, & h4, & h5, & h6': { mt: 1, mb: 1 }
        }}>
          <RequirementText
            text={message.content}
            requirements={requirements}
            onRequirementClick={(requirementId) => {
              // Handle requirement click - could navigate to requirement details
              console.log('Requirement clicked:', requirementId);
              // TODO: Implement navigation to requirement details
            }}
            sx={{
              '& p': { m: 0, mb: 1 },
              '& pre': { my: 1, p: 0 },
              '& code': { 
                bgcolor: 'background.paper', 
                px: 0.5, 
                py: 0.25, 
                borderRadius: 0.5,
                fontFamily: 'monospace',
                fontSize: '0.85rem'
              },
              '& ul, & ol': { pl: 3, mb: 1 },
              '& h1, & h2, & h3, & h4, & h5, & h6': { mt: 1, mb: 1 }
            }}
          />
        </Box>
        
        {message.isStreaming && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Generating response...
            </Typography>
          </Box>
        )}
      </Paper>
    );
  };

  const renderThinkingSteps = useCallback(() => {
    if (!showThinking || thinkingSteps.length === 0) return null;

    return (
      <Box sx={{ mb: 2, px: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ fontWeight: 'medium', display: 'flex', alignItems: 'center', color: 'primary' }}>
              <InfoOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} />
              Thinking Process ({thinkingSteps.length} steps)
            </Typography>
            {currentIteration > 0 && maxIterations > 0 && (
              <Chip
                size="small"
                label={`Iteration ${currentIteration}/${maxIterations}`}
                color="primary"
                variant="outlined"
                sx={{ ml: 1, height: 20, fontSize: '0.65rem' }}
              />
            )}
          </Box>
          <Tooltip title="Hide thinking steps">
            <IconButton size="small" onClick={toggleThinking}>
              <VisibilityOffIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {currentIteration > 0 && maxIterations > 0 && (
          <Box sx={{ mb: 1.5, mt: 0.5 }}>
            <LinearProgress
              variant="determinate"
              value={(currentIteration / maxIterations) * 100}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {thinkingSteps.map((step, index) => (
            <ThinkingStep
              key={step.id || index}
              step={step}
              stepNumber={index + 1}
              totalSteps={thinkingSteps.length}
              requirements={requirements}
            />
          ))}
        </Box>
      </Box>
    );
    // `requirements` is read inside (passed down to each ThinkingStep for
    // requirement-id linkification) so it belongs here: omitting it meant a
    // thinking panel already on screen kept rendering against the requirement
    // list from the render that last produced this callback. The identity of
    // this callback is not consumed by any effect/memo — it is only invoked
    // during render — so widening the deps cannot restart or re-subscribe
    // anything; at worst it is recreated a render more often.
  }, [showThinking, thinkingSteps, currentIteration, maxIterations, toggleThinking, requirements]);
  
  const renderErrorMessage = (errorObj) => {
    if (!errorObj) return null;
    
    return (
      <Box sx={{ mb: 2, mx: 2 }}>
        <ErrorAlert
          error={errorObj}
          onClose={handleErrorDismiss}
          severity={errorObj.isRateLimit ? "warning" : "error"}
          sx={{
            maxWidth: '85%',
            ml: 0,
            mr: 'auto',
            '& .MuiAlert-message': { overflow: 'hidden' }
          }}
        />
      </Box>
    );
  };
  
  if (!expanded) {
    return (
      <Box 
        sx={{ 
          position: 'fixed', 
          bottom: 16, 
          right: 16, 
          zIndex: 1000 
        }}
      >
        <Tooltip title="Open Chat">
          <IconButton 
            color="primary" 
            size="large"
            onClick={() => setExpanded(true)}
            sx={{ 
              bgcolor: 'background.paper',
              boxShadow: 2,
              '&:hover': {
                bgcolor: 'background.paper',
                boxShadow: 3
              }
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path fill="currentColor" d="M12 1c-6.627 0-12 4.208-12 9.399 0 3.356 2.246 6.301 5.625 7.963-1.299 4.45-1.333 4.47-1.113 4.599.276.161.634-.005 5.357-3.311.692.097 1.404.148 2.131.148 6.627 0 12-4.208 12-9.399s-5.373-9.399-12-9.399zm5.779 13.05c-.951 1.234-2.76 2.143-4.779 2.143-1.428 0-2.557-.388-3.406-.771-1.654-.742-2.586-1.627-2.586-1.627-.126-.118-.302-.161-.468-.104-.01.003-.023.01-.033.014v-.004c-.073.03-.133.085-.174.149-.031.051-.045.106-.051.164-.001.007-.003.014-.003.021 0 .001 0 .002 0 .003 0 .111.049.216.129.289.018.017.032.03.051.045 0 0 2.106 1.85 5.341 2.373 3.257.524 7.979-.64 7.979-5.489 0-2.058-1.19-3.811-3-4.955-.172-.108-.395-.06-.51.106-.116.167-.075.392.091.501 1.469.908 2.419 2.267 2.419 3.854 0 1.933-.904 3.043-1.015 3.205z"/>
            </svg>
          </IconButton>
        </Tooltip>
      </Box>
    );
  }
  
  return (
    <Box 
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: height,
        width: '100%',
        position: { xs: 'fixed', md: 'relative' },
        bottom: { xs: 0, md: 'auto' },
        right: { xs: 0, md: 'auto' },
        left: { xs: 0, md: 'auto' },
        zIndex: { xs: 1000, md: 1 },
        borderRadius: { xs: '12px 12px 0 0', md: 2 },
        overflow: 'hidden',
        boxShadow: { xs: 3, md: 1 }
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          minWidth: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle2" component="div" sx={{ flexShrink: 0 }}>
            Assistant
          </Typography>

          <FormControl size="small" sx={{
            minWidth: 0,
            flex: 1,
            maxWidth: 180,
            '& .MuiOutlinedInput-root': { height: 30 },
          }}>
            <Select
              labelId="model-select-label"
              value={currentModel}
              onChange={(e) => changeModel(e.target.value)}
              size="small"
              displayEmpty
              sx={{
                '.MuiSelect-select': {
                  py: 0.5,
                  fontSize: '0.8125rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }
              }}
              MenuProps={{
                slotProps: {
                  paper: { sx: { maxHeight: 200 } },
                },
              }}
            >
              {models.map((model) => (
                <MenuItem key={model.id} value={model.id} sx={{ fontSize: '0.875rem' }}>
                  {model.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box>
          <Tooltip title="Menu">
            <IconButton
              size="small"
              edge="end"
              color="inherit"
              onClick={handleMenuOpen}
              data-testid="chat-menu"
            >
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={handleMenuClose}
          >
            {/* Provider + API-key controls live here (kept out of the cramped
                header). They open their own popovers anchored to their icons. */}
            <Box sx={{ px: 1.5, py: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ProviderSelector />
              <Typography variant="body2">AI source</Typography>
            </Box>
            <Box sx={{ px: 1.5, py: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ApiKeySettings />
              <Typography variant="body2">LLM API key</Typography>
            </Box>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem onClick={handleToggleThinking}>
              {showThinking ? <VisibilityOffIcon fontSize="small" sx={{ mr: 1 }} /> : <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />}
              {showThinking ? 'Hide thinking process' : 'Show thinking process'}
            </MenuItem>
            <MenuItem onClick={() => {
              setShowProcessArea(prev => !prev);
              handleMenuClose();
            }}>
              <TimelineIcon fontSize="small" sx={{ mr: 1 }} />
              {showProcessArea ? 'Hide process area' : 'Show process area'}
            </MenuItem>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem onClick={handleExportChat} disabled={messages.length === 0}>
              <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
              Export conversation
            </MenuItem>
            <MenuItem onClick={handleClearHistory}>
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
              Clear history
            </MenuItem>
            <MenuItem onClick={handleNewSession}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" style={{ marginRight: 8 }}>
                <path fill="currentColor" d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 9h7V2l-2.35 4.35z"/>
              </svg>
              New session
            </MenuItem>
            <MenuItem onClick={() => {
              setExpanded(false);
              handleMenuClose();
            }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" style={{ marginRight: 8 }}>
                <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
              Minimize
            </MenuItem>
          </Menu>
        </Box>
      </Box>
      
      {/* #170: polite live region announcing AI thinking/streaming state to
          screen readers. Visually hidden — the sighted UI already shows a
          spinner/thinking panel; this is the assistive-tech equivalent. */}
      <Box
        component="span"
        role="status"
        aria-live="polite"
        data-testid="ai-status-live"
        sx={{
          position: 'absolute', width: 1, height: 1, p: 0, m: '-1px',
          overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
        }}
      >
        {aiStatus}
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          p: 2,
          overflow: 'auto',
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {messages.length === 0 && !streamingMessage && !inlineErrorMessage ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              opacity: 0.7,
              flexGrow: 1
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" style={{ marginBottom: 16 }}>
              <path fill="currentColor" d="M12 1c-6.627 0-12 4.208-12 9.399 0 3.356 2.246 6.301 5.625 7.963-1.299 4.45-1.333 4.47-1.113 4.599.276.161.634-.005 5.357-3.311.692.097 1.404.148 2.131.148 6.627 0 12-4.208 12-9.399s-5.373-9.399-12-9.399zm5.779 13.05c-.951 1.234-2.76 2.143-4.779 2.143-1.428 0-2.557-.388-3.406-.771-1.654-.742-2.586-1.627-2.586-1.627-.126-.118-.302-.161-.468-.104-.01.003-.023.01-.033.014v-.004c-.073.03-.133.085-.174.149-.031.051-.045.106-.051.164-.001.007-.003.014-.003.021 0 .001 0 .002 0 .003 0 .111.049.216.129.289.018.017.032.03.051.045 0 0 2.106 1.85 5.341 2.373 3.257.524 7.979-.64 7.979-5.489 0-2.058-1.19-3.811-3-4.955-.172-.108-.395-.06-.51.106-.116.167-.075.392.091.501 1.469.908 2.419 2.267 2.419 3.854 0 1.933-.904 3.043-1.015 3.205z"/>
            </svg>
            <Typography variant="body2" align="center" sx={{ color: 'text.secondary' }}>
              No messages yet. Ask something to get started!
            </Typography>
          </Box>
        ) : (
          <Box sx={{ flexGrow: 1 }}>
            {messages.map(renderMessage)}
            
            {renderThinkingSteps()}
            
            <ProcessArea
              thinkingSteps={thinkingSteps}
              currentIteration={currentIteration}
              maxIterations={maxIterations}
              isVisible={showProcessArea}
            />
            
            {streamingMessage && renderMessage(streamingMessage)}
            
            {inlineErrorMessage && renderErrorMessage(inlineErrorMessage)}
            
            <div ref={messagesEndRef} />
          </Box>
        )}
      </Box>
      
      <Box sx={{ p: 2, bgcolor: 'background.paper', minHeight: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, width: '100%' }}>
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            minRows={1}
            maxRows={6}
            variant="outlined"
            placeholder="Type your message..."
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            disabled={loading}
            size="small"
            autoComplete="off"
            slotProps={{
              htmlInput: { 'data-testid': 'chat-input' },
              input: {
                style: {
                  resize: 'vertical',
                  minHeight: 'auto'
                }
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                alignItems: 'flex-start',
                '& textarea': {
                  resize: 'none',
                  overflow: 'auto',
                  minHeight: 'auto !important'
                },
                '& fieldset': {
                  minHeight: 'auto'
                }
              },
              '& .MuiInputBase-input': {
                minHeight: 'auto !important'
              }
            }}
          />
          <IconButton
            onClick={handleSubmit}
            color="primary"
            disabled={loading || !inputValue.trim()}
            data-testid="chat-send"
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
              '&.Mui-disabled': {
                bgcolor: 'action.disabledBackground',
                color: 'action.disabled'
              }
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

ChatInterface.propTypes = {
  context: PropTypes.object,
  onContextChange: PropTypes.func,
  height: PropTypes.string,
  initialOpen: PropTypes.bool,
  onResponse: PropTypes.func,
  requirements: PropTypes.array
};

export default ChatInterface;
